import { useLayoutEffect } from 'react';
import { normalizePathname } from '../../config/staticPageMeta';
import type { Article } from '../../utils/articleApi';

function truncateMetaDescription(s: string, max = 155): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function absoluteUrl(href: string, origin: string): string {
  try {
    return new URL(href, origin).href;
  } catch {
    return href;
  }
}

/**
 * 記事詳細のブラウザタブ名・meta description・OGP / Twitter を記事に合わせて更新する。
 * タイトル／description の復帰は StaticRouteDocumentHead が担当。ここでは OGP のみクリーンアップする。
 */
export default function ArticleDocumentHead({ article }: { article: Article }) {
  useLayoutEffect(() => {
    const origin = window.location.origin.replace(/\/$/, '');
    const norm = normalizePathname(window.location.pathname);
    const pageUrl = norm === '/' ? `${origin}/` : `${origin}${norm}`;
    const tabTitle = `${article.title} | Social Trip`;
    const desc = truncateMetaDescription(
      article.excerpt?.trim() ? article.excerpt : article.title
    );
    const imageUrl = absoluteUrl(article.image, origin);

    const metaDesc = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;

    document
      .querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')
      .forEach((n) => n.remove());

    document.title = tabTitle;
    if (metaDesc) {
      metaDesc.setAttribute('content', desc);
    }

    const head = document.head;
    const addOg = (property: string, content: string) => {
      const m = document.createElement('meta');
      m.setAttribute('property', property);
      m.setAttribute('content', content);
      head.appendChild(m);
    };
    const addTw = (name: string, content: string) => {
      const m = document.createElement('meta');
      m.setAttribute('name', name);
      m.setAttribute('content', content);
      head.appendChild(m);
    };

    addOg('og:title', tabTitle);
    addOg('og:description', desc);
    addOg('og:url', pageUrl);
    addOg('og:type', 'article');
    addOg('og:image', imageUrl);
    addOg('og:site_name', 'Social Trip');
    addTw('twitter:card', 'summary_large_image');
    addTw('twitter:title', tabTitle);
    addTw('twitter:description', desc);
    addTw('twitter:image', imageUrl);

    return () => {
      document
        .querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')
        .forEach((n) => n.remove());
    };
  }, [article.id, article.title, article.excerpt, article.image]);

  return null;
}
